import { useEffect, useState } from "react";
import { Button, Form, Modal, Spinner } from "react-bootstrap";
import { useCompleteBooking } from "../../../../../api/fleet-management/bookings/booking.hooks";
import { useUploadMediaMutation } from "../../../../../api/fleet-management/media/media.hooks";
import { getFleetUploadRelativeUrl } from "../../../../../api/fleet-management/api";

const createPenaltyRow = () => ({
  amount: "",
  mode: "",
  transactionId: "",
  reason: "",
  remarks: "",
  attachment: "",
});

const getCurrentDateTimeLocalValue = () => {
  const now = new Date();
  const timezoneOffsetInMs = now.getTimezoneOffset() * 60000;
  return new Date(now.getTime() - timezoneOffsetInMs).toISOString().slice(0, 16);
};

const CompleteBookingModal = ({
  show,
  booking,
  onClose,
  onSuccess,
  onError,
}) => {
  const { mutate: completeBooking, isLoading: isSubmitting } = useCompleteBooking();
  const { mutateAsync: uploadMedia, isLoading: isUploadingAttachment } = useUploadMediaMutation();
  const [actualDropoffDateTime, setActualDropoffDateTime] = useState("");
  const [penalties, setPenalties] = useState([]);
  const [uploadingPenaltyIndex, setUploadingPenaltyIndex] = useState(null);

  useEffect(() => {
    if (!show) return;
    setActualDropoffDateTime(getCurrentDateTimeLocalValue());
    setPenalties([]);
    setUploadingPenaltyIndex(null);
  }, [show]);

  const customerName = booking?.customerSnapshot?.name || booking?.fullName || "customer";

  const addPenalty = () => {
    setPenalties((prev) => [...prev, createPenaltyRow()]);
  };

  const removePenalty = (index) => {
    setPenalties((prev) => prev.filter((_, itemIndex) => itemIndex !== index));
  };

  const updatePenalty = (index, key, value) => {
    setPenalties((prev) =>
      prev.map((penalty, itemIndex) =>
        itemIndex === index ? { ...penalty, [key]: value } : penalty
      )
    );
  };

  const handlePenaltyAttachmentUpload = async (index, event) => {
    const file = event?.target?.files?.[0];
    if (!file) return;

    try {
      setUploadingPenaltyIndex(index);
      const uploadResult = await uploadMedia(file);
      const relativePath = getFleetUploadRelativeUrl(uploadResult, 0);

      if (!relativePath) {
        throw new Error("Uploaded attachment path not found.");
      }

      updatePenalty(index, "attachment", relativePath);
    } catch (uploadError) {
      onError?.(uploadError?.message || "Failed to upload penalty attachment.");
    } finally {
      setUploadingPenaltyIndex(null);
      if (event?.target) {
        event.target.value = "";
      }
    }
  };

  const handleConfirm = () => {
    if (!booking?._id) return;
    if (!actualDropoffDateTime) {
      onError?.("Booking completed at date/time is required.");
      return;
    }

    const normalizedPenalties = penalties
      .map((penalty) => ({
        amount: penalty?.amount === "" ? "" : Number(penalty.amount),
        mode: (penalty?.mode || "").trim(),
        transactionId: (penalty?.transactionId || "").trim(),
        reason: (penalty?.reason || "").trim(),
        remarks: (penalty?.remarks || "").trim(),
        attachment: (penalty?.attachment || "").trim(),
      }))
      .filter(
        (penalty) =>
          penalty.amount !== "" ||
          Boolean(penalty.mode) ||
          Boolean(penalty.transactionId) ||
          Boolean(penalty.reason) ||
          Boolean(penalty.remarks) ||
          Boolean(penalty.attachment)
      );

    const invalidPenalty = normalizedPenalties.find(
      (penalty) =>
        !penalty.amount ||
        !penalty.reason ||
        !penalty.mode ||
        !["cash", "online"].includes(penalty.mode) ||
        (penalty.mode === "online" && !penalty.transactionId)
    );

    if (invalidPenalty) {
      onError?.("Each penalty must include amount, reason, mode, and transaction ID for online mode.");
      return;
    }

    completeBooking(
      {
        bookingId: booking._id,
        payload: {
          actualDropoffDateTime: new Date(actualDropoffDateTime).toISOString(),
          penalties: normalizedPenalties,
        },
      },
      {
        onSuccess: () => {
          onSuccess?.("Booking completed successfully.");
          onClose?.();
        },
        onError: (completeError) => {
          onError?.(completeError?.response?.data?.message || "Failed to complete booking.");
        },
      }
    );
  };

  return (
    <Modal show={show} onHide={onClose} centered>
      <Modal.Header closeButton={!isSubmitting}>
        <Modal.Title>Complete Booking</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <p className="mb-2">
          Are you sure you want to mark this booking as completed?
        </p>
        <p className="mb-0 text-muted">
          Customer: <strong>{customerName}</strong>
        </p>

        <Form.Group className="mt-3">
          <Form.Label>
            Booking Completed At <span className="text-danger">*</span>
          </Form.Label>
          <Form.Control
            type="datetime-local"
            value={actualDropoffDateTime || ""}
            onChange={(e) => setActualDropoffDateTime(e.target.value)}
            disabled={isSubmitting}
          />
          <Form.Text className="text-muted">
            This will be sent as actual dropoff date/time.
          </Form.Text>
        </Form.Group>

        <div className="d-flex align-items-center justify-content-between mt-4 mb-2">
          <Form.Label className="mb-0">Penalties (Optional)</Form.Label>
          <Button
            variant="outline-danger"
            size="sm"
            onClick={addPenalty}
            disabled={isSubmitting || isUploadingAttachment}
          >
            Add Penalty
          </Button>
        </div>

        {penalties.map((penalty, index) => (
          <div key={`penalty-${index}`} className="border rounded p-3 mb-2">
            <div className="d-flex align-items-center justify-content-between mb-2">
              <strong className="small">Penalty #{index + 1}</strong>
              <Button
                variant="link"
                size="sm"
                className="p-0 text-danger"
                onClick={() => removePenalty(index)}
                disabled={isSubmitting || isUploadingAttachment}
              >
                Remove
              </Button>
            </div>

            <Form.Group className="mb-2">
              <Form.Label className="mb-1">Amount <span className="text-danger">*</span></Form.Label>
              <Form.Control
                type="number"
                min="0"
                value={penalty.amount}
                onChange={(e) => updatePenalty(index, "amount", e.target.value)}
                disabled={isSubmitting || isUploadingAttachment}
              />
            </Form.Group>

            <Form.Group className="mb-2">
              <Form.Label className="mb-1">Mode <span className="text-danger">*</span></Form.Label>
              <Form.Select
                value={penalty.mode}
                onChange={(e) => {
                  const nextMode = e.target.value;
                  updatePenalty(index, "mode", nextMode);
                  if (nextMode === "cash") {
                    updatePenalty(index, "transactionId", "");
                  }
                }}
                disabled={isSubmitting || isUploadingAttachment}
              >
                <option value="">Select mode</option>
                <option value="cash">cash</option>
                <option value="online">online</option>
              </Form.Select>
            </Form.Group>

            {penalty.mode === "online" && (
              <Form.Group className="mb-2">
                <Form.Label className="mb-1">Transaction ID <span className="text-danger">*</span></Form.Label>
                <Form.Control
                  type="text"
                  value={penalty.transactionId}
                  onChange={(e) => updatePenalty(index, "transactionId", e.target.value)}
                  disabled={isSubmitting || isUploadingAttachment}
                />
              </Form.Group>
            )}

            <Form.Group className="mb-2">
              <Form.Label className="mb-1">Reason <span className="text-danger">*</span></Form.Label>
              <Form.Control
                type="text"
                value={penalty.reason}
                onChange={(e) => updatePenalty(index, "reason", e.target.value)}
                disabled={isSubmitting || isUploadingAttachment}
              />
            </Form.Group>

            <Form.Group className="mb-2">
              <Form.Label className="mb-1">Remarks</Form.Label>
              <Form.Control
                type="text"
                value={penalty.remarks}
                onChange={(e) => updatePenalty(index, "remarks", e.target.value)}
                disabled={isSubmitting || isUploadingAttachment}
              />
            </Form.Group>

            <Form.Group>
              <Form.Label className="mb-1">Attachment</Form.Label>
              <Form.Control
                type="file"
                onChange={(e) => handlePenaltyAttachmentUpload(index, e)}
                disabled={isSubmitting || isUploadingAttachment}
              />
              <Form.Text className="text-muted d-block">
                {uploadingPenaltyIndex === index
                  ? "Uploading attachment..."
                  : penalty.attachment
                  ? `Uploaded: ${penalty.attachment}`
                  : "Optional receipt/supporting document"}
              </Form.Text>
            </Form.Group>
          </div>
        ))}
      </Modal.Body>

      <Modal.Footer>
        <Button variant="light" onClick={onClose} disabled={isSubmitting || isUploadingAttachment}>
          Cancel
        </Button>
        <Button variant="success" onClick={handleConfirm} disabled={isSubmitting || isUploadingAttachment}>
          {isSubmitting ? (
            <>
              <Spinner as="span" animation="border" size="sm" className="me-2" />
              Completing...
            </>
          ) : (
            "Complete Booking"
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default CompleteBookingModal;
